import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { UserRole } from "../config/userRole";

const GlobalContext = createContext();
export const useGlobal = () => useContext(GlobalContext);

export const GlobalProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [container, setContainer] = useState([]);
  const [planters, setPlanters] = useState([]);
  const [accessoriesCat, setAccessoriesCat] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const hasFetchedProducts = useRef(false);

  const webToken = process.env.REACT_APP_WEB_TOKEN;
  // const [token, setToken] = useState(() => localStorage.getItem("userToken") || webToken); 

  const [token, setToken] = 
  useState(() => (localStorage.getItem("userToken") && localStorage.getItem("uservarified") !== null) ? localStorage.getItem("userToken") : webToken);

  const cartLength = useMemo(() => cart?.length || 0, [cart]);

  const getDefaultVariationId = useCallback((product) => {
    const variations = product?.product_variations || [];
    if (!variations.length) return null;

    if (UserRole.userRole === "distributor") {
      const withPack = variations.find(
        (v) => v?.pack_price != null && v?.pack_qty != null
      );
      return (withPack || variations[0])?.id ?? null;
    }

    const inStock = variations.find(
      (v) => v?.size_id !== null && v?.stock_qty !== 0
    );
    const withSize = variations.find((v) => v?.size_id !== null);
    return (inStock || withSize || variations[0])?.id ?? null;
  }, []);

  const getDistributorUnitPrice = useCallback((variation, fallbackPrice) => {
    const packQty = Number(variation?.pack_qty || 0);
    const packPrice = Number(variation?.pack_price || 0);
    if (packQty > 0 && packPrice > 0) {
      return packPrice / packQty;
    }
    return Number(fallbackPrice || 0);
  }, []);

  const getCartItemVariation = useCallback((item) => {
    if (item?.product_variation) return item.product_variation;
    if (Array.isArray(item?.product_variations) && item?.prod_variation_id) {
      return item.product_variations.find((v) => v.id === item.prod_variation_id) || null;
    }
    return null;
  }, []);

  const getDistributorLineTotal = useCallback((item) => {
    const variation = getCartItemVariation(item);
    const packQty = Number(variation?.pack_qty || item?.pack_qty || item?.product?.pack_qty || 0);
    const packPrice = Number(variation?.pack_price || item?.pack_price || item?.product?.pack_price || 0);
    if (packQty > 0 && packPrice > 0) {
      return (Number(item.quantity) / packQty) * packPrice;
    }
    const unitPrice = Number(item.price ?? item.sale_price ?? 0);
    return unitPrice * Number(item.quantity || 0);
  }, [getCartItemVariation]);

  // 🛒 Merge local cart items with server
  const mergeLocalCartWithServer = useCallback(async (userToken) => {
    const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
    if (!localCart.length) return;

    if (UserRole.userRole === "distributor") {
      localStorage.removeItem("localCart");
      return;
    }

    try {
      for (const item of localCart) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/add_to_cart`, {
          product_id: item.id,
          prod_variation_id: item.prod_variation_id,
          quantity: item.quantity || 1,
          price: item.price ?? item.sale_price,
        }, {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
      }
      localStorage.removeItem("localCart");
    } catch (err) {
      console.error("Merge cart error:", err);
    }
  }, []);

  // 🧾 Fetch Cart
  const fetchCart = useCallback(async () => {
    try {
      const userToken = localStorage.getItem("userToken");
      if (!userToken) {
        if (UserRole.userRole === "distributor") {
          localStorage.removeItem("localCart");
          setCart([]);
          return;
        }
        const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
        setCart(Array.isArray(localCart) ? localCart : []);
        return;
      }

      await mergeLocalCartWithServer(userToken);
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_cart`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      const items = data.cart_items || [];
      const normalized =
        UserRole.userRole === "distributor"
          ? items.map((item) => {
              const variation = getCartItemVariation(item);
              const unitPrice = getDistributorUnitPrice(
                variation,
                item.price ?? item.sale_price
              );
              return { ...item, price: unitPrice };
            })
          : items;
      setCart(normalized);
    } catch (err) {
      console.error("Fetch cart error:", err);
    } finally {
      setCartLoading(false);
    }
  }, [mergeLocalCartWithServer, getCartItemVariation, getDistributorUnitPrice]);

  // 💖 Fetch Wishlist
  const fetchWishlist = useCallback(async () => {
    if (!token || token === webToken) {
      setWishlistLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_wishlist`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (data.status) {
        setWishlist(data.wishlist);
      }
    } catch (err) {
      console.error("Fetch wishlist error:", err);
    } finally {
      setWishlistLoading(false);
    }
  }, [token]);

  // 🛍️ Fetch Products
  const fetchProducts = useCallback(async () => {
    if (hasFetchedProducts.current) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/get_active_products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      // console.log('data', data);
      if (data.status) {
        setProducts(data.products);
        hasFetchedProducts.current = true;
      }
    } catch (err) {
      console.error("Fetch products error:", err);
    }
  }, [token]);

  // 🧠 Add to Cart
  const addToCart = useCallback(async (product, selectedSize, qtyToAdd) => {
    const userToken = localStorage.getItem("userToken");

    if (!product || !product.id) {
      console.error("Invalid product:", product);
      return false;
    }

    const resolvedVariationId = selectedSize || getDefaultVariationId(product);
    const selectedVariation = product.product_variations?.find(
      (v) => v.id === resolvedVariationId
    );

    const isDistributor = UserRole.userRole === "distributor";
    // Prefer variation-level pack pricing; fall back to the product's own base
    // pack pricing for simple bulk-only products that have no variations at all
    // (e.g. added via Wishlist, which has no local quantity of its own to pass in).
    const packQty = Number(selectedVariation?.pack_qty || product?.pack_qty || 0);
    const packPrice = Number(selectedVariation?.pack_price || product?.pack_price || 0);

    const resolvedQty =
      isDistributor
        ? Number(qtyToAdd) || (packQty > 0 ? packQty : 1)
        : 1;

    const unitPrice = isDistributor
      ? (packQty > 0 && packPrice > 0
          ? packPrice / packQty
          : Number(selectedVariation?.sale_price || product.sale_price || 0))
      : (selectedVariation?.sale_price || product.sale_price);

    const payload = {
      product_id: product.id,
      prod_variation_id: resolvedVariationId,
      quantity: resolvedQty,
      price: unitPrice,
    };

    if (userToken) {

      // if(cart.length > 0) {
      //   const isInCart = cart.some((item) => item.product_id === product.id);
      //   console.log('not selected', isInCart);
      //   if (isInCart) return;
      // }

      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/add_to_cart`, payload, {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        if (!data?.status) {
          console.error("Add to cart rejected:", data?.error_message);
          return false;
        }
        await fetchCart();
        return true;
      } catch (err) {
        console.error("Add to cart failed:", err);
        return false;
      }
    } else {
      if (UserRole.userRole === "distributor") {
        console.warn("Distributor local cart is disabled.");
        return false;
      }
      const localCart = JSON.parse(localStorage.getItem("localCart") || "[]");
      const exists = localCart.find(
        (item) =>
          item.product_id === product.id &&
          item.prod_variation_id === resolvedVariationId
      );
      const updatedCart = exists
        ? localCart.map((item) =>
            item.product_id === product.id &&
            item.prod_variation_id === resolvedVariationId
              ? { ...item, quantity: item.quantity + resolvedQty, price: unitPrice }
              : item
          )
        : [
            ...localCart,
            {
              ...product,
              quantity: resolvedQty,
              price: unitPrice,
              prod_variation_id: resolvedVariationId,
              product_id: product.id,
            },
          ];

      localStorage.setItem("localCart", JSON.stringify(updatedCart));
      setCart(updatedCart);
      return true;
    }
  }, [cart, fetchCart, getDefaultVariationId, getDistributorUnitPrice]);

  // 💘 Add/Remove Wishlist
  const addToWishlist = useCallback(async (product) => {
    const isInWishlist = wishlist.some((item) => item.product_id === product.id);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/add_to_wishlist`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          product_id: product.id,
          quantity: 1,
          prod_variation_id: product.prod_variation_id,
        }),
      });
      const data = await res.json();

      if (data.status) {
        setWishlist((prev) =>
          isInWishlist
            ? prev.filter((item) => item.product_id !== product.id)
            : [...prev, product]
        );
      }
    } catch (err) {
      console.error("Wishlist error:", err);
    }
  }, [wishlist, token]);

  // 🔄 Increase/Decrease Quantity
  const increaseQuantity = (productId) => {
    setCart((prev) =>
      prev.map((item) => item.id === productId ? { ...item, quantity: item.quantity + 1 } : item)
    );
  };

  const decreaseQuantity = (productId) => {
    setCart((prev) =>
      prev
        .map((item) => item.id === productId ? { ...item, quantity: item.quantity - 1 } : item)
        .filter((item) => item.quantity > 0)
    );
  };

  // 💵 Cart Totals
  const calculateSummary = useCallback(() => {
    // Ensure cart is an array
    const items = Array.isArray(cart) ? cart : []; // Fallback to empty array if not an array
  
    // Calculate total amount, total MRP, and subtotal
    const totalAmount = items.reduce((sum, item) => {
      if (UserRole.userRole === "distributor") {
        return sum + getDistributorLineTotal(item);
      }
      const unitPrice = Number(item.price ?? item.sale_price ?? 0);
      return sum + unitPrice * item.quantity;
    }, 0);
    const totalMRP = UserRole.userRole === "distributor"
      ? totalAmount
      : items.reduce((sum, item) => {
          const unitRegular = Number(item.regular_price ?? item.price ?? item.sale_price ?? 0);
          return sum + unitRegular * item.quantity;
        }, 0);
    const subtotal = totalAmount; // You could use the same calculation for subtotal as totalAmount
    const savings = totalMRP - subtotal;
  
    return { totalMRP, subtotal, savings, totalAmount };
  }, [cart]);
  
  const { totalMRP, subtotal, savings, totalAmount } = calculateSummary();

  // for footer categories

  const fetchAllCategories = async () => {
    if (!token) return;

    setLoadingCategories(true);

    try {
      // MEN
      const cachedPlanters = sessionStorage.getItem("planters");
      if (cachedPlanters) {
        setPlanters(JSON.parse(cachedPlanters));
      } else {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_slug_data/planters`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res?.data?.category_data?.child_categories ?? [];
        setPlanters(data);
        sessionStorage.setItem("planters", JSON.stringify(data));
      }

      // CONTAINER
      const cachedContainer = sessionStorage.getItem("container");
      if (cachedContainer) {
        setContainer(JSON.parse(cachedContainer));
      } else {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_slug_data/container`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res?.data?.category_data?.child_categories ?? [];
        setContainer(data);
        sessionStorage.setItem("container", JSON.stringify(data));
      }

      // ACCESSORIES
      const cachedAcc = sessionStorage.getItem("accessories");
      if (cachedAcc) {
        setAccessoriesCat(JSON.parse(cachedAcc));
      } else {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_slug_data/accessories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res?.data?.category_data?.child_categories ?? [];
        setAccessoriesCat(data);
        sessionStorage.setItem("accessories", JSON.stringify(data));
      }
    } catch (err) {
      console.error("Category fetch error:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Load categories when token is available
  useEffect(() => {
    if (token) fetchAllCategories();
  }, [token]);
  
  

  // 🔐 Logout
  const logout = () => {
    setToken(webToken);
    localStorage.removeItem("userToken");
    setCart([]);
    setWishlist([]);
  };

  // 🧠 Initial Load
  useEffect(() => {
    fetchWishlist();
    fetchCart();
    fetchProducts();
  }, [fetchWishlist, fetchCart, fetchProducts]);

  return (
    <GlobalContext.Provider
      value={{
        token,
        setToken,
        products,
        wishlist,
        wishlistLoading,
        setWishlist,
        addToWishlist,
        cart,
        cartLength,
        cartLoading,
        setCart,
        addToCart,
        increaseQuantity,
        decreaseQuantity,
        totalMRP,
        subtotal,
        savings,
        totalAmount,
        mergeLocalCartWithServer,
        planters,
        container,
        accessoriesCat,
        loadingCategories,
        logout,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
